class CompareProducts extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.attributeKey = `compare-${this.collection}`

    this.compareSet = new Set(
      (this.dataset.products || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    )
  }

  setupListeners () {
    super.setupListeners()

    this.handleCompareToggle = this.handleCompareToggle.bind(this)
    this.listeners.add(this.toggle, 'click', this.handleCompareToggle)

    this.handleMobileTriggerToggle = this.handleMobileTriggerToggle.bind(this)
    this.listeners.add(
      this.mobileTrigger,
      'click',
      this.handleMobileTriggerToggle,
    )
    this.listeners.add(
      this.els.close.element,
      'click',
      this.handleMobileTriggerToggle,
    )
    this.listeners.add(
      this.externalEls.backdrop.element,
      'click',
      this.handleMobileTriggerToggle,
    )

    this.handleAdd = this.handleAdd.bind(this)
    this.listeners.add(document, 'click', this.handleAdd)

    this.handleRemove = this.handleRemove.bind(this)
    this.listeners.add(document, 'click', this.handleRemove)

    this.handleExpanderClick = this.handleExpanderClick.bind(this)
    this.listeners.add(
      this.els.expander.element,
      'click',
      this.handleExpanderClick,
    )

    this.handleResize = this.updateCompareVisibility.bind(this)
    window.addEventListener('resize', this.handleResize)

    this.handleCompareTriggerClick = this.handleCompareTriggerClick.bind(this)
    this.listeners.add(
      this.els['modal-opener'].element,
      'click',
      this.handleCompareTriggerClick,
    )
  }

  handleCompareTriggerClick () {
    const collection = this.dataset.collection
    if (!collection) return

    const params = new URLSearchParams({ collection })

    window.location.href = `/pages/compare?${params.toString()}`
  }

  async handleRemove (event) {
    const internalTrigger = event.target.closest(
      '[data-compare-products-el="remove"]',
    )
    const externalTrigger = event.target.closest(
      '[data-compare-products-external-el="remove"]',
    )

    const trigger = internalTrigger || externalTrigger
    if (!trigger) return

    const productHandle = trigger.dataset.productHandle
    if (!productHandle) return

    // Remove product
    this.compareSet.delete(productHandle)

    this.updateTriggerState(trigger, false)

    // Sync products data attribute
    this.dataset.products = Array.from(this.compareSet).join(',')

    // Update count element
    this.updateCountElement()

    // Update modal opener state
    this.updateModalOpenerState()

    try {
      await window.BAO.Cart.updateCartAttributes({
        [this.attributeKey]: this.dataset.products,
      })
    } catch (e) {
      console.error('Failed to update compare attribute:', e)
    }

    // Update summary
    await this.updateSummary()

    // Update product card buttons
    this.updateProductCardButtons()

    // Update visibility
    this.updateCompareVisibility()

    // Fire custom event if it was an external remove
    if (externalTrigger) {
      if (this.compareSet.size <= 1) {
        window.drawers.activeDrawerKey = ''
      }

      document.dispatchEvent(
        new CustomEvent('compare:externalRemove', {
          bubbles: true,
          detail: {
            collection: this.collection,
            compareSize: this.compareSet.size,
          },
        }),
      )
    }
  }

  async handleAdd (event) {
    const trigger = event.target.closest(this.externalEls.add.selector)
    if (!trigger) return

    const productHandle = trigger.dataset.productHandle
    if (!productHandle) return

    let isActive
    if (this.compareSet.has(productHandle)) {
      // Remove product
      this.compareSet.delete(productHandle)
      isActive = false
    } else {
      // Add product unless limit has been reached
      if (this.compareSet.size >= this.limit) {
        console.warn(`Compare limit of ${this.limit} reached.`)
        return
      }
      this.compareSet.add(productHandle)
      isActive = true
    }

    this.updateTriggerState(trigger, isActive)

    // Sync products data attribute
    this.dataset.products = Array.from(this.compareSet).join(',')

    // Update count element
    this.updateCountElement()

    // Update modal opener state
    this.updateModalOpenerState()

    try {
      await window.BAO.Cart.updateCartAttributes({
        [this.attributeKey]: this.dataset.products,
      })
    } catch (e) {
      console.error('Failed to update compare attribute:', e)
    }

    // Update summary
    await this.updateSummary()

    // Update product card buttons
    this.updateProductCardButtons()

    // Update visibility
    this.updateCompareVisibility()
  }

  handleExpanderClick (event) {
    const button = event.currentTarget
    const expanded = button.getAttribute('aria-expanded') === 'true'

    this.setCompareExpanded(!expanded)
  }

  setCompareExpanded (expanded) {
    const bottom = this.els.bottom.element
    const backdrop = this.externalEls.backdrop.element
    const expander = this.els.expander?.element

    if (expanded) {
      bottom.removeAttribute('hidden')
      backdrop.removeAttribute('hidden')
    } else {
      bottom.setAttribute('hidden', '')
      backdrop.setAttribute('hidden', '')
    }

    if (expander) {
      expander.setAttribute('aria-expanded', String(expanded))
    }

    this.externalEls.grid.element.classList.toggle(
      'clc-ProductGrid-compareExpanded',
      expanded,
    )
  }

  updateCompareVisibility () {
    const button = this.toggle
    const togglePressed = button.getAttribute('aria-pressed') === 'true'

    const isMobile = window.BAO.MediaBreakpoints.isMd === false

    const mobileTrigger = this.mobileTrigger
    const mobileTriggerPressed =
      mobileTrigger.getAttribute('aria-pressed') === 'true'

    const compareVisible = isMobile
      ? mobileTriggerPressed && togglePressed
      : togglePressed

    this.hidden = !compareVisible

    if (isMobile && compareVisible) {
      this.els.bottom.element.removeAttribute('hidden')
      this.externalEls.backdrop.element.removeAttribute('hidden')
    }

    if (togglePressed === true) {
      this.externalEls.grid.element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }

    this.externalEls.grid.element.classList.toggle(
      'clc-ProductGrid-showCompare',
      togglePressed,
    )

    this.externalEls.grid.element.classList.toggle(
      'clc-ProductGrid-showCompareBackground',
      mobileTriggerPressed && togglePressed,
    )

    if (compareVisible && this.hasItemsToCompare()) {
      this.setCompareExpanded(true)
    }

    if (!compareVisible && !isMobile) {
      this.setCompareExpanded(false)
    }
  }

  handleCompareToggle () {
    const button = this.toggle
    const isPressed = button.getAttribute('aria-pressed') === 'true'

    button.setAttribute('aria-pressed', String(!isPressed))
    button.setAttribute('aria-expanded', String(!isPressed))

    const mobileTrigger = this.mobileTrigger
    mobileTrigger.setAttribute('aria-hidden', String(isPressed))

    // Update visibility
    this.updateCompareVisibility()
  }

  handleMobileTriggerToggle () {
    const button = this.mobileTrigger
    const isPressed = button.getAttribute('aria-pressed') === 'true'

    button.setAttribute('aria-pressed', String(!isPressed))

    // Update visibility
    this.updateCompareVisibility()
  }

  updateTriggerState (button, isActive) {
    button.setAttribute('aria-pressed', String(isActive))
  }

  updateCountElement () {
    this.els['product-count'].elements?.forEach((el) => {
      el.textContent = `(${this.compareSet.size}/${this.limit})`
    })

    this.externalEls['compare-count'].elements?.forEach((el) => {
      el.textContent =
        this.compareSet.size > 1 ? `(${this.compareSet.size})` : ''
    })
  }

  updateModalOpenerState () {
    this.els['modal-opener'].elements?.forEach((el) => {
      el.disabled = this.compareSet.size < 2
    })

    this.els['modal-opener-text'].elements?.forEach((el) => {
      if (this.compareSet.size === 0) {
        el.textContent = window.theme.strings.compare.addAtLeast2Products
      } else if (this.compareSet.size === 1) {
        el.textContent = window.theme.strings.compare.addAtLeast1Product
      } else {
        el.textContent = window.theme.strings.compare.compareTheseItems
      }
    })
  }

  async updateSummary () {
    const res = await window.BAO.sectionRenderer.fetchSingle(
      'compare-products-summary',
      `/collections/${this.collection}`,
    )

    const newDOM = window.BAO.sectionRenderer.convertToDOM(res)

    const section = newDOM.querySelector('.shopify-section') || newDOM

    this.els.summary.element.innerHTML = section.innerHTML
  }

  updateProductCardButtons () {
    const buttons = document.querySelectorAll(this.externalEls.add.selector)
    const limitReached = this.compareSet.size >= this.limit

    buttons.forEach((btn) => {
      const handle = btn.dataset.productHandle
      const isActive = this.compareSet.has(handle)

      // Set pressed state (active or not)
      this.updateTriggerState(btn, isActive)

      // Disable if limit reached and not already active
      btn.disabled = limitReached && !isActive
    })
  }

  hasItemsToCompare () {
    return this.compareSet.size > 0
  }

  get mobileTrigger () {
    return this.externalEls['mobile-trigger'].element
  }

  get toggle () {
    return this.externalEls.toggle.element
  }

  get collection () {
    return this.dataset.collection
  }

  get limit () {
    return parseInt(this.dataset.limit, 10) || 3
  }
}

if (!customElements.get('compare-products')) {
  customElements.define('compare-products', CompareProducts)
}
